
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ActionAuthLogin, ActionAuthLogout } from '../auth/auth.reducer';
//import { BrowserModule } from '@angular/platform-browser';
import { environment as env } from '@env/environment';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { tap } from "rxjs/internal/operators/tap";
import { catchError } from 'rxjs/operators';
import { IConfirmResponse } from '@app/core/interfaces/confirm/IConfirmResponse';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/ConfirmDialog';
import { AuthGuardService } from '../auth/auth-guard.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

export interface PasswordLoginOtpChallenge {
  RequiresOtp: boolean;
  ChallengeId: number;
  MaskedEmail: string;
  ExpiresAtUtc: string;
  ResendAvailableAtUtc: string;
}

@Injectable()
export class AuthenticationService {
  public token: string;
  private userString: string;
  private apiBaseUrl: string;
  private apiAuthUrl: string;
  private appId: number;
  private httpOptions: any;
  private authGuardService: AuthGuardService;
  public userData = {} as any;
  private accountCompletionSettings: any = { orgNumber: null, email: null, response: null, needsCompletion: false, redirectToSettings: false, organizationLinkingResolved: false };
  public userDataIsUpdated: boolean = false;
  private suppressAccountCompletionCheck: boolean = false;
  private accountCompletionCheckTimeout: any = null;
  private accountCompletionCheckVersion: number = 0;
  private impersonationTimeout: any = null;

  constructor(
    public dialog: MatDialog,
    private http: HttpClient,
    authGuardService: AuthGuardService,
    private localStorageService: LocalStorageService,
    private store: Store<any>,
    private router: Router,
    public snackBar: MatSnackBar) {

    this.subscribeToUpdates();
    this.authGuardService = authGuardService;
    this.apiBaseUrl = env.hostApi;
    this.apiAuthUrl = env.hostAuth;
    this.appId = env.appId;

    var userDataFromStore = JSON.parse(localStorage.getItem('ANMS-AUTH'));

    if (userDataFromStore != undefined && userDataFromStore.Jwt != undefined) {
      this.token = userDataFromStore.Jwt;
      this.setHttpOptions();
      this.userData = userDataFromStore;
      // Organization settings may have changed since this session was stored.
      if (this.userData.User?.Organization) this.userData.User.Organization.IsApiCustomer = false;
      // The HTTP interceptor depends on this service; finish constructing it first.
      queueMicrotask(() => this.getCurrentUser().subscribe());
      this.scheduleImpersonationExpiry();
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'ANMS-AUTH' && event.newValue !== event.oldValue) {
        location.reload();
      }
    });

  }

  ngOnInit(): void {
  }

  private subject = new Subject<any>();

  userUpdated(message: string) {
    this.subject.next({ text: message });
  }

  onUserUpdate(): Observable<any> {
    return this.subject.asObservable();
  }

  private subscribeToUpdates(): void {
    this.onUserUpdate().subscribe((update) => {
      if (this.suppressAccountCompletionCheck) {
        this.suppressAccountCompletionCheck = false;
        return;
      }

      if (update) {
        this.checkForAccountCompletion();
      }
    });
  }

  login(username: string = null, password: string = null, bankIdpersonalNumber: string = null, emailEncrypted: string = null, userId: string = null, pid: string = null, appId: number = null): Observable<any> {

    let payload: { Email: string; Password: string } = {
      Email: username,
      Password: password
    };

    // Convert the payload object to JSON
    let payloadJson: string = JSON.stringify(payload);

    var url = this.apiAuthUrl;
    const queryParams: string[] = [];

    if (bankIdpersonalNumber)
      queryParams.push("bankIdpersonalNumber=" + encodeURIComponent(bankIdpersonalNumber));

    if (bankIdpersonalNumber && emailEncrypted)
      queryParams.push("emailEncrypted=" + encodeURIComponent(emailEncrypted));

    if (pid)
      queryParams.push("pid=" + encodeURIComponent(pid));

    if (appId != null)
      queryParams.push("appid=" + encodeURIComponent(appId.toString()));

    if (queryParams.length > 0)
      url = url + "?" + queryParams.join("&");

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    return this.http.post<any>(url, payloadJson, httpOptions)
      .pipe(
        tap((response: any) => {
          if (this.isJwtLoginResponse(response)) {
            this.saveToken(response, username);
          }
        }),
        catchError(this.handleError('login', null)
        ));
  }

  verifyLoginOtp(challengeId: number, otpCode: string): Observable<any> {
    const payloadJson = JSON.stringify({
      ChallengeId: challengeId,
      OtpCode: otpCode
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    return this.http.post<any>(
      `${this.apiAuthUrl}/verify-otp?appId=${encodeURIComponent(this.appId.toString())}`,
      payloadJson,
      httpOptions)
      .pipe(
        tap((response: any) => {
          if (this.isJwtLoginResponse(response)) {
            this.saveToken(response, "");
          }
        }),
        catchError(this.handleError('verify-otp', null))
      );
  }

  loginBankIdPassive(personalNumber: string, userId: string = null): Observable<boolean> {

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    var url = this.apiBaseUrl + "BankId/" + personalNumber + "/AuthenticatePassive";

    if (userId != null)
      url = url + "&userId=" + userId;

    return this.http.get<any>(url, httpOptions)
      .pipe(
        tap((response: any) =>
          this.saveToken(response, response.userName)),
        catchError(this.handleError('bankid_passive')
        ));
  }

  onUserLoggedIn(response: any) {
    this.saveToken(response, "");
  }

  startImpersonation(targetUserId: string): Observable<any> {
    return this.http.post<any>(
      this.apiBaseUrl + 'impersonation/start',
      { TargetUserId: targetUserId },
      { withCredentials: true }
    ).pipe(
      tap((response: any) => this.saveToken(response, ""))
    );
  }

  stopImpersonation(): Observable<any> {
    return this.http.post<any>(
      this.apiBaseUrl + 'impersonation/stop',
      {},
      { withCredentials: true }
    ).pipe(
      tap((response: any) => this.saveToken(response, ""))
    );
  }

  isImpersonating(): boolean {
    return this.userData?.IsImpersonating === true;
  }

  getImpersonationState(): any {
    if (!this.isImpersonating()) {
      return null;
    }

    return {
      actorDisplayName: this.userData.ActorDisplayName,
      targetDisplayName: [this.userData?.User?.FirstName, this.userData?.User?.LastName]
        .filter(value => !!value)
        .join(' '),
      targetEmail: this.userData?.User?.Email,
      expiresAtUtc: this.userData.ExpiresAtUtc
    };
  }

  createAccount(user: any): Observable<string> {

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    var data = JSON.stringify(user);

    return this.http.post(this.apiBaseUrl + 'Users?appId=' + this.appId, data, {
      ...httpOptions,
      responseType: 'text'
    })
      .pipe(
        tap((response: any) =>
          console.log(response)
        )
      );
  }

  handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      let config = new MatSnackBarConfig();
      config.duration = 15000;

      const errorBody = error?.error;
      const errorMessage = [errorBody?.ExceptionMessage, errorBody?.message, errorBody?.Message, errorBody?.detail, errorBody?.title, errorBody]
        .find(value => typeof value === 'string' && value.trim().length > 0);

      if (error.status == 400) {
        this.snackBar.open(this.translateErrors(errorMessage), undefined, config);
      }
      else if (error.status == 409) {
        this.snackBar.open("E-postadressen är redan registrerad på ett annat konto.", undefined, config);
      }
      else if (error.statusText == 'Unknown Error') {
        this.snackBar.open("Något gick fel. Försök gärna logga in igen, och kontakta support om problemet kvarstår.", undefined, config);
      }
      else if (error.status == 500) {
        this.snackBar.open(this.translateErrors(errorMessage), undefined, config);
      }
      else {
        this.snackBar.open(this.translateErrors(errorMessage), undefined, config);
      }

      return of(result as T);
    };
  }

  translateErrors(errorString) {
    if (!errorString || typeof errorString !== 'string') {
      return 'Något gick fel. Försök igen.';
    }

    const translations = {
      "Passwords must be at least 6 characters.": "Lösenord måste vara minst 6 tecken långt.",
      "Passwords must have at least one non alphanumeric character.": "Lösenord måste innehålla minst ett specialtecken.",
      "Passwords must have at least one lowercase ('a'-'z').": "Lösenord måste innehålla minst en liten bokstav.",
      "Passwords must have at least one uppercase ('A'-'Z').": "Lösenord måste innehålla minst en stor bokstav."
    };

    return errorString
      .split(";")
      .map(msg => msg.trim())
      .filter(msg => msg.length > 0)
      .map(msg => translations[msg] || msg)
      .join(', ');
  }


  getCurrentUser(): Observable<any> {

    var url = this.apiBaseUrl + 'Users/Me';
    var bearerToken = 'bearer ' + this.token;

    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': bearerToken
      })
    };

    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        tap((response: any) => this.onFinished(response)),
        catchError(
          (err: any) => {
            console.log(err)
            return new Observable<any>();

          }
        ));
  }

  searchOrganizationById(id: string): Observable<any> {

    var url = this.apiBaseUrl + 'Organizations/Search/' + id;
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        tap((response: any) => this.onRetrieved(response)),
        catchError(
          (err: any) => {
            return new Observable<any>();
          }
        ));
  }

  onRetrieved(response: any) {
    console.log(response);
  }


  checkForAccountCompletion(): void {
    if (this.accountCompletionCheckTimeout != null) {
      clearTimeout(this.accountCompletionCheckTimeout);
      this.accountCompletionCheckTimeout = null;
    }

    const requestVersion = ++this.accountCompletionCheckVersion;

    this.accountCompletionCheckTimeout = setTimeout(() => {
      const url = this.apiBaseUrl + 'Users/Me/NeedsCompletion';

      this.setHttpOptions();

      this.http.get<any>(url, this.httpOptions).subscribe(
        (response) => {
          if (requestVersion !== this.accountCompletionCheckVersion || this.updatingUser || this.userDataIsUpdated) {
            return;
          }

          this.handleAccountCompletion(response);
        },
        (error) => {
          console.error('Error occurred during HTTP call:', error);
        });
      this.accountCompletionCheckTimeout = null;
    }, 1500);
  }

  updatingUser = false;

  updateCurrentUserData(email: string, phone: string, organizationNumber: string = null, sampoolenIsMainEmployer: string = null, appId: string = "2", referralCode: string = '', campaignCode: string = ''): void {
    
	    if (this.updatingUser)
	      return

	    this.updatingUser = true
      this.accountCompletionCheckVersion++;
      if (this.accountCompletionCheckTimeout != null) {
        clearTimeout(this.accountCompletionCheckTimeout);
        this.accountCompletionCheckTimeout = null;
      }

    email = email == null ? '' : email;
    phone = phone == null ? '' : phone;
    organizationNumber = organizationNumber == null ? '' : organizationNumber;
    sampoolenIsMainEmployer = sampoolenIsMainEmployer == null ? '' : sampoolenIsMainEmployer;

    const url = this.apiBaseUrl + 'Users/Me';
    var bearerToken = 'bearer ' + this.token;

    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': bearerToken
      })
    };

    var sampoolenIsMainEmployerBool = sampoolenIsMainEmployer.toLowerCase() == "ja" ? true : false;
    var payload = JSON.parse('{"SystemAppId": "' + appId + '", "Email": "' + email + '","Phone": "' + phone + '", "OrganizationNr": "' + organizationNumber + '", "SampoolenIsMainEmployer": "' + sampoolenIsMainEmployer + '", "ReferralCode":"' + referralCode + '", "CampaignCode":"' + campaignCode + '"}');

	    this.http.patch<any>(url, payload, this.httpOptions).subscribe(
	      (response: any) => {
	        this.updatingUser = false;
          if (response != null && response.Jwt != null) {
            this.saveToken(response, "");
          }
	        this.userDataUpdated(response);
	      },
      (errorResponse) => {
        this.updatingUser = false;
        let errorMessage = errorResponse && errorResponse.error
          ? (errorResponse.error.Message || errorResponse.error.message || errorResponse.error)
          : null;
        if (typeof errorMessage === 'string') {
          const firstLine = errorMessage.split('\n')[0];
          errorMessage = firstLine.replace(/^System\.Exception:\s*/i, '').trim();
        }
        if (errorResponse?.status === 409 && errorResponse.error?.code === 'OrganizationLinkingConflict') {
          this.accountCompletionSettings.orgNumber = null;
          this.accountCompletionSettings.organizationLinkingResolved = true;
          this.accountCompletionSettings.redirectToSettings = false;
          // Show the conflict without retrying the rejected organization update.
          this.openOrganizationNotice(errorMessage).then(() => {
            this.router.navigateByUrl('/about');
          });
          return;
        }
        if (typeof errorMessage !== 'string') {
          errorMessage = 'Uppgifterna kunde inte sparas. Försök igen senare.';
        }
        if (errorMessage === 'PersonalNumberExistsInDb') {
          errorMessage = 'Det finns redan ett konto med detta personnummer';
        }
        if (errorMessage && errorMessage.includes("Epostadressen")) {
          this.accountCompletionSettings.userEmail = null;
          this.handleAccountCompletion(null, errorMessage);
          return;
        }
        if (appId === '2') {
          if (organizationNumber) {
            this.accountCompletionSettings.orgNumber = null;
            const message = errorResponse.error?.code === 'OrganizationLookupFailed'
              ? 'Företaget kunde inte hämtas. Kontrollera organisationsnumret och försök igen.'
              : 'Företagskopplingen kunde inte sparas. Försök igen eller avbryt.';
            this.openGetOrganizationnumberModal(message, organizationNumber).then(number => {
              this.accountCompletionSettings.orgNumber = number;
              this.updateCurrentUserData(email, phone, number, sampoolenIsMainEmployer, appId, referralCode, campaignCode);
            }).catch(() => {
              this.accountCompletionSettings.organizationLinkingResolved = true;
              this.accountCompletionSettings.redirectToSettings = false;
              this.router.navigateByUrl('/about');
            });
          } else {
            this.openOrganizationNotice(errorMessage, 'Uppgifterna kunde inte sparas');
          }
          return;
        }
        this.handleAccountCompletion(null, errorMessage);
      }
    );

  }

  async handleAccountCompletion(response, errorMessage = null): Promise<void> {
    try {

      console.log('account completion')

      // Set the completioninfo for later use if function is calles again
      if (response != null)
        this.accountCompletionSettings.response = response;

      var errorType = errorMessage != null && errorMessage.includes("Epostadressen") ? "email" : null;
      var userHasConnectedOrganization = this.userHasConnectedOrganization();
      var organizationCompletionRequired = this.appId == 2 && !userHasConnectedOrganization;

      if (this.accountCompletionSettings.response.NeedsCompletion || organizationCompletionRequired) {
        this.accountCompletionSettings.needsCompletion = true;

        if ((this.accountCompletionSettings.response.CompleteEmail && this.accountCompletionSettings.userEmail == null) || errorType == "email") {
          this.accountCompletionSettings.userEmail = await this.openGetUserEmailModal(errorMessage);
        }

        const referralBonusEnabled = false;

        if (referralBonusEnabled && ((this.appId == 1 && this.accountCompletionSettings.referralCode == null && this.accountCompletionSettings.response.AskForCampaignCode) || errorType == "referralCode")) {
          this.accountCompletionSettings.referralCode = await this.openGetReferralCodelModal(errorMessage);
        }

        if (this.appId == 1 && (this.accountCompletionSettings.campaignCode == null && this.accountCompletionSettings.response.AskForCampaignCode) || errorType == "campaignCode") {
          this.accountCompletionSettings.campaignCode = await this.openGetCampaignCodelModal(errorMessage);
        }

        if ((this.accountCompletionSettings.response.CompleteEmail && this.accountCompletionSettings.userPhone == null) || errorType == "phone")
          this.accountCompletionSettings.userPhone = await this.openGetUserPhoneModal(errorMessage);

        if (this.appId == 2 && ((organizationCompletionRequired && this.accountCompletionSettings.orgNumber == null) || errorType == "orgNumber")) {
          try {
            this.accountCompletionSettings.orgNumber = await this.openGetOrganizationnumberModal();
          } catch {
            // The personal account is already created. Organization linking can be completed later.
            this.accountCompletionSettings.orgNumber = null;
            this.accountCompletionSettings.organizationLinkingResolved = true;
          }
        }

        if (this.appId == 1 && ((this.accountCompletionSettings.response.CompleteSampoolenIsMainEmployer && this.accountCompletionSettings.sampoolenIsMainEmployer == null) || errorType == "employer")) {

          this.accountCompletionSettings.sampoolenIsMainEmployer = await this.openGetEmployerModal();

        }

        // Sampoolen
        if (this.appId == 1) {
          const shouldUpdate =
            this.accountCompletionSettings.userEmail != null ||
            this.accountCompletionSettings.userPhone != null ||
            this.accountCompletionSettings.sampoolenIsMainEmployer != null ||
            (referralBonusEnabled && this.accountCompletionSettings.referralCode != null) ||
            this.accountCompletionSettings.campaignCode != null;

          if (shouldUpdate) {
            this.accountCompletionSettings.redirectToSettings = this.accountCompletionSettings.needsCompletion;
            this.updateCurrentUserData(
              this.accountCompletionSettings.userEmail,
              this.accountCompletionSettings.userPhone,
              null,
              this.accountCompletionSettings.sampoolenIsMainEmployer,
              this.appId.toString(),
              referralBonusEnabled ? this.accountCompletionSettings.referralCode : '',
              this.accountCompletionSettings.campaignCode
            );
          }

        }
        // Samfinans
        else if (this.appId == 2) {
          var emailValidates = !this.accountCompletionSettings.response.CompleteEmail || (this.accountCompletionSettings.response.CompleteEmail && this.accountCompletionSettings.userEmail != null)
          //Only update if email or organization is set
          if (this.accountCompletionSettings.userEmail != null || this.accountCompletionSettings.orgNumber != null) {
            if (emailValidates) {

              this.accountCompletionSettings.redirectToSettings = this.accountCompletionSettings.needsCompletion &&
                !this.accountCompletionSettings.organizationLinkingResolved;
              this.updateCurrentUserData(this.accountCompletionSettings.userEmail, this.accountCompletionSettings.userPhone, this.accountCompletionSettings.orgNumber, this.accountCompletionSettings.sampoolenIsMainEmployer, this.appId.toString(), referralBonusEnabled ? this.accountCompletionSettings.referralCode : '');
            }
          }
        }

      }




    } catch (error) {
      // Handle any error that occurred during the modal operations
      alert('An error occurred during account completion.');
	  }
	}

  private clearAccountCompletionInputs(): void {
    this.accountCompletionSettings.userEmail = null;
    this.accountCompletionSettings.userPhone = null;
    this.accountCompletionSettings.orgNumber = null;
    this.accountCompletionSettings.sampoolenIsMainEmployer = null;
    this.accountCompletionSettings.referralCode = null;
    this.accountCompletionSettings.campaignCode = null;
    this.accountCompletionSettings.needsCompletion = false;
    this.accountCompletionSettings.response = null;
  }

  private userHasConnectedOrganization(): boolean {
    return Number(this.userData?.User?.Organization?.Id || 0) > 0;
  }

  async openGetUserPhoneModal(errorMessage = null): Promise<string> {

    return new Promise((resolve, reject) => {

      // Trigger modal to fill email address
      var config = this.createAccountCompletionDialogConfig();
      config.data.hideInput = false;
      config.data.requireInput = false;
      config.data.hideCancelButton = true;
      config.data.inputTitle = 'Ange ditt telefonnummer';
      config.data.confirmButtonText = "Spara";
      config.data.confirmButtonIcon = "check";
      config.data.errorText = errorMessage;
      config.data.disableClose = true;


      if (this.appId == 1) {
        config.data.headline = "Telefon";
        config.data.text = "Ange ditt telefonnummer så kan vi starta igång sen!";
      }
      if (this.appId == 2) {
        config.data.headline = "Telefon";
        config.data.text = "Ange ditt telefonnummer så kan vi starta igång sen!";
      }

      let dialogRef = this.dialog.open(ConfirmDialogComponent, config);

      dialogRef.afterClosed().subscribe((result: IConfirmResponse) => {
        dialogRef = null;

        if (result.Confirmed) {
          resolve(result.ResponseText);
        }
        else {
          reject("Vänligen ange en annan epostadress!");
        }

      });

    });

  }

  async openGetUserEmailModal(errorMessage = null): Promise<string> {

    return new Promise((resolve, reject) => {

      // Trigger modal to fill email address
      var config = this.createAccountCompletionDialogConfig();
      config.data.hideInput = false;
      config.data.requireInput = false;
      config.data.hideCancelButton = true;
      config.data.inputTitle = 'Ange din epostadress';
      config.data.confirmButtonText = "Spara";
      config.data.confirmButtonIcon = "check";
      config.data.errorText = errorMessage;
      config.data.disableClose = true;

      if (this.appId == 1) {
        config.data.headline = "Hej! Välkommen till SAMpoolen";
        config.data.text = "Ange din epostadress så kan vi starta igång sedan.";

      }
      if (this.appId == 2) {
        config.data.headline = "Hej! Välkommen till SAMekonomi";
        config.data.text = "Vi ser att det är första gången du besöker oss. Låt oss börja med att skapa ditt personkonto:";
      }

      let dialogRef = this.dialog.open(ConfirmDialogComponent, config);

      dialogRef.afterClosed().subscribe((result: IConfirmResponse) => {
        dialogRef = null;

        if (result.Confirmed) {
          if (!this.validateEmail(result.ResponseText)) {
            reject("Ange en giltig epostadress!");
          }
          else {
            resolve(result.ResponseText);
          }
        }
        else {
          reject("Vänligen ange en annan epostadress!");
        }

      });

    });

  }

  async openGetReferralCodelModal(errorMessage = null): Promise<string> {

    return new Promise((resolve, reject) => {

      // Trigger modal to fill email address
      var config = this.createAccountCompletionDialogConfig();
      config.data.hideInput = false;
      config.data.requireInput = false;
      config.data.hideCancelButton = true;
      config.data.inputTitle = 'Ange kod';
      config.data.confirmButtonText = "Använd koden";
      config.data.confirmButtonIcon = "check";
      config.data.errorText = errorMessage;
      config.data.disableClose = true;

      config.data.headline = "Värvningskod";
      config.data.text = "Om du fått en värvningskod av en vän så anger du den här. Klicka på spara för att fortsätta";

      config.data.showSecondaryConfirmButton = true;
      config.data.secondaryButtonTitle = 'Jag har ingen kod';
      config.data.secondaryConfirmButtonIcon = 'cancel';

      let dialogRef = this.dialog.open(ConfirmDialogComponent, config);

      dialogRef.afterClosed().subscribe((result: IConfirmResponse) => {
        dialogRef = null;
        
        if (result.Confirmed) {

          if (result.ResponseText == undefined)
            result.ResponseText = '';

          resolve(result.ResponseText);
        }
        else {
          reject("Vänligen ange en annan epostadress!");
        }

      });

    });

  }

  async openGetCampaignCodelModal(errorMessage = null): Promise<string> {

    return new Promise((resolve, reject) => {

      // Trigger modal to fill email address
      var config = this.createAccountCompletionDialogConfig();
      config.data.hideInput = false;
      config.data.requireInput = false;
      config.data.hideCancelButton = true;
      config.data.inputTitle = 'Ange kod';
      config.data.confirmButtonText = "Använd koden";
      config.data.confirmButtonIcon = "check";
      config.data.errorText = errorMessage;
      config.data.disableClose = true;

      config.data.headline = "Kampanjkod";
      config.data.text = "Om du har en kampanjkod så anger du den här, klicka på spara för att fortsätta";

      config.data.showSecondaryConfirmButton = true;
      config.data.secondaryButtonTitle = 'Jag har ingen kod';
      config.data.secondaryConfirmButtonIcon = 'cancel';

      let dialogRef = this.dialog.open(ConfirmDialogComponent, config);

      dialogRef.afterClosed().subscribe((result: IConfirmResponse) => {
        dialogRef = null;
        
        if (result.Confirmed) {

          if (result.ResponseText == undefined)
            result.ResponseText = '';

          resolve(result.ResponseText);
        }
        else {
          reject("Vänligen ange en annan epostadress!");
        }

      });

    });

  }

  private async openOrganizationNotice(message: string, headline = 'Företagskopplingen stoppades'): Promise<void> {
    const config = this.createAccountCompletionDialogConfig();
    config.data.hideInput = true;
    config.data.requireInput = false;
    config.data.hideCancelButton = true;
    config.data.showSecondaryConfirmButton = false;
    config.data.disableConfirmButton = false;
    config.data.headline = headline;
    config.data.text = message;
    config.data.confirmButtonText = 'Stäng';
    config.data.confirmButtonIcon = 'close';
    await this.dialog.open(ConfirmDialogComponent, config).afterClosed().toPromise();
  }

  async openGetOrganizationnumberModal(errorMessage: string = null, initialNumber = ''): Promise<string> {
    let number = initialNumber;
    while (true) {
      const config = this.createAccountCompletionDialogConfig();
      config.data.hideInput = false;
      config.data.requireInput = true;
      config.data.hideCancelButton = false;
      config.data.inputTitle = 'Organisationsnummer';
      config.data.headline = 'Organisation';
      config.data.confirmButtonText = 'Spara';
      config.data.confirmButtonIcon = 'check';
      config.data.text = 'Vilket företag vill du koppla? Ange organisationsnumret så söker vi efter företaget.';
      config.data.errorText = errorMessage;
      config.data.initialInput = number;
      config.data.clearErrorOnInput = true;
      config.data.inputPattern = '(?:[0-9][ -]*){10}(?:(?:[0-9][ -]*){2})?';
      config.data.invalidInputText = 'Ange ett organisationsnummer med 10 eller 12 siffror.';
      config.data.disableConfirmButton = false;

      const result: IConfirmResponse = await this.dialog.open(ConfirmDialogComponent, config).afterClosed().toPromise();
      if (!result?.Confirmed) {
        throw new Error('Organization linking canceled');
      }
      number = (result.ResponseText || '').trim();
      const normalizedNumber = number.replace(/[ -]/g, '');
      if (!/^(?:[0-9]{10}|[0-9]{12})$/.test(normalizedNumber)) {
        errorMessage = 'Ange ett organisationsnummer med 10 eller 12 siffror.';
        continue;
      }

      let company;
      try {
        company = await this.searchOrganizationById(normalizedNumber).toPromise();
        if (!company?.Name) {
          errorMessage = 'Företaget kunde inte hämtas. Kontrollera organisationsnumret och försök igen.';
          continue;
        }
      } catch {
        errorMessage = 'Företaget kunde inte hämtas. Kontrollera organisationsnumret och försök igen.';
        continue;
      }

      if (company.IsRegisteredInOrganization) {
        const email = company.ExistingOrganizationMasterAdministratorEmail || 'okänd e-postadress';
        await this.openOrganizationNotice(
          'Företaget är redan registrerat. Kontakta företagets huvudadministratör (' + email +
          ') för hjälp med åtkomst. Du kan också kontakta support@samgruppen.se.'
        );
        throw new Error('Organization already registered');
      }
      if (await this.openConfirmOrganizationModal(company.Name)) {
        this.accountCompletionSettings.organizationLinkingResolved = false;
        return normalizedNumber;
      }
      errorMessage = null;
    }
  }

  openGetEmployerModal(errorMessage: string = null): Promise<string> {
    return new Promise((resolve, reject) => {
      var config = this.createAccountCompletionDialogConfig();
      config.disableClose = true;
      config.data.hideInput = false;
      config.data.requireInput = false;
      config.data.hideCancelButton = true;
      config.data.inputTitle = 'Svarsalternativ: Ja/Nej';
      config.data.headline = "Arbetsgivare";
      config.data.useYesNo = true;
      config.data.confirmButtonText = "Spara";
      config.data.confirmButtonIcon = "check";
      config.data.text = "<p><strong>Är lönen från SAMpoolen din huvudsakliga inkomst?</strong></p><p><strong>Ja, detta kommer vara din största inkomst:</strong><br>Välj JA. Vi drar då skatt enligt din personliga skattetabell, för att undvika att du får restskatt.</p><p><strong>Nej, detta är en sidoinkomst:</strong><br>Välj NEJ. Vi drar då 30%, vilket oftast ger dig lite mer pengar kvar i fickan vid varje utbetalning.</p>";
      config.data.errorText = errorMessage;
      config.data.disableClose = true;


      let dialogRef = this.dialog.open(ConfirmDialogComponent, config);

      dialogRef.afterClosed().subscribe(async (result: IConfirmResponse) => {
        dialogRef = null;

        if (result.Confirmed) {
          try {
            var response = result.ResponseText.toLowerCase();
            if (response == "ja" || response == "nej")
            {
              resolve(response);
            } else {
              this.openGetEmployerModal().then(resolve).catch(reject);
            }

          } catch (error) {
            console.error(error);
            reject('Employer input failed');
          }
        } else {
          reject('Employer input canceled');
        }

      });
    });
  }

  userDataUpdated(response) {

    this.userDataIsUpdated = true;
    if (this.appId == 2 && !this.userHasConnectedOrganization()) {
      // Personal details can be saved even when organization linking was declined.
      this.clearAccountCompletionInputs();
      this.accountCompletionSettings.organizationLinkingResolved = true;
      this.accountCompletionSettings.redirectToSettings = false;
      this.suppressAccountCompletionCheck = true;
      this.userUpdated('');
      this.router.navigateByUrl('/about');
      return;
    }

    // Trigger modal to fill email address
    var config = this.createAccountCompletionDialogConfig();
    config.data.hideInput = true;
    config.data.requireInput = false;
    config.data.hideCancelButton = true;
    config.data.headline = "Registrering färdig!";
    config.data.showSecondaryConfirmButton = false;
    config.data.confirmButtonText = "Ok";
    config.data.confirmButtonIcon = "check";

    if (this.appId == 1) {
      config.data.text = "Tack för ditt förtroende. Tveka inte höra av dig om det dyker upp några frågor och/eller funderingar!";
    }
    if (this.appId == 2) {
      config.data.text = 'Ditt konto är kopplat till företaget. Du kan nu använda SAMekonomi.';
    }


    let dialogRef = this.dialog.open(ConfirmDialogComponent, config);

	    dialogRef.afterClosed().subscribe((result: IConfirmResponse) => {
	      dialogRef = null;
        this.clearAccountCompletionInputs();
        this.suppressAccountCompletionCheck = true;

	      this.getCurrentUser()
	        .subscribe(
          data => {
            var userString = localStorage.getItem('ANMS-AUTH')
            var currentUserObject = JSON.parse(userString);
            currentUserObject.User = data;

            localStorage.setItem('ANMS-AUTH', JSON.stringify(currentUserObject));
            this.userData = currentUserObject;
            this.userDataIsUpdated = true;
            this.userUpdated('');
            if (this.appId == 2 && this.accountCompletionSettings.organizationLinkingResolved) {
              this.router.navigateByUrl('/about');
              return;
            }
            if (
              this.accountCompletionSettings.redirectToSettings &&
              this.router.url.startsWith("/settings")
            ) {
              this.accountCompletionSettings.redirectToSettings = false;
              this.userUpdated('settings-refresh');
            }
            else if (
              this.accountCompletionSettings.redirectToSettings &&
              !this.router.url.startsWith("/settings") &&
              !this.router.url.startsWith("/invoice-tool")
            ) {
              this.accountCompletionSettings.redirectToSettings = false;
              this.router.navigateByUrl("/settings");
            }
          },
          error => {
            alert('err');
          });

    });

  }

  openConfirmOrganizationModal(orgName: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      var config = this.createAccountCompletionDialogConfig();
      config.data.hideInput = true;
      config.data.requireInput = false;
      config.data.hideCancelButton = false;
      config.data.inputTitle = 'Bekräfta';
      config.data.headline = "Bekräfta organisation";
      config.data.confirmButtonText = "Ja";
      config.data.confirmButtonIcon = "check";
      config.data.text = "Är " + orgName + " rätt organisation?";

      let dialogRef = this.dialog.open(ConfirmDialogComponent, config);

      dialogRef.afterClosed().subscribe((result: IConfirmResponse) => {
        dialogRef = null;

        if (result.Confirmed) {
          resolve(true);
        } else {
          resolve(false); // Resolve with false indicating the user canceled the confirmation
        }
      });
    });
  }

  validateEmail(email: string): boolean {
    // Regular expression for validating email addresses
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Test the email against the regex
    return emailRegex.test(email);
  }

  isSamekonomiOrganizationLinkingResolved(): boolean {
    return this.userHasConnectedOrganization() || this.accountCompletionSettings.organizationLinkingResolved === true;
  }

  private createAccountCompletionDialogConfig(): any {
    const config = JSON.parse(JSON.stringify(this.config));
    config.panelClass = 'account-completion-dialog-panel';
    config.width = '480px';
    config.data.accountCompletion = true;
    config.maxWidth = 'calc(100vw - 32px)';
    config.height = 'auto';
    config.maxHeight = 'calc(100vh - 32px)';
    return config;
  }

  config = {
    disableClose: true,
    panelClass: 'full-screen-modal',
    hasBackdrop: true,
    backdropClass: '',
    width: '500px',
    maxWidth: '100vw',
    height: '100%',
    position: {
      top: '',
      bottom: '',
      left: '',
      right: ''
    },
    data: {
      invoiceItemGroup: {},
      customerTemplate: {},
      contact: { IDNumber: null },
      companyId: 0,
      status: {},
      customerCountryName: null,
    },
    dataTemplate: {
      Id: 0,
      Country: { Id: 1 },
      Type: {},
      HoursWorked: undefined,
      StartDate: undefined,
      EndDate: undefined,
      Comment: undefined,
      InvoiceId: 0
    }
  };

  onFinished(response: any) {

    var userString = localStorage.getItem('ANMS-AUTH')
    var currentUserObject = JSON.parse(userString);
    this.userData = currentUserObject;
    this.userData.User = response;

    localStorage.setItem('ANMS-AUTH', JSON.stringify(this.userData));
    this.userUpdated("");
  }

  private ensureCurrentUserIsLoaded(): void {
    if (this.userHasResolvedRoles()) {
      this.userUpdated('');
      return;
    }

    this.getCurrentUser().subscribe({
      error: () => {
        this.userUpdated('');
      }
    });
  }

  private userHasResolvedRoles(): boolean {
    return Array.isArray(this.userData?.User?.Roles);
  }

  setHttpOptions() {

    var bearerToken = 'bearer ' + this.token;

    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': bearerToken
      })
    };
  }

  currentUserHasCoordinationNumber() {
    return this.userData.User.HasCoordinationNumber;
  }

  currentUserUserId() {
    return (this.userData != null && this.userData.User != null) ? this.userData.User.Id : {};
  }

  currentUserEmailEncrypted() {
    return (this.userData != null && this.userData.User != null) ? this.userData.User.EmailEncrypted : {};
  }

  getCurrentUserOrganization() {
    return this.userData.User.Organization;
  }

  setCurrentUserOrganizationCompany(company) {

    if (company == null)
      return;

    this.userData.User.Organization.Company = company;
    var storedUserData = JSON.parse(localStorage.getItem('ANMS-AUTH'));
    storedUserData.User.Organization.Company = company;

    localStorage.setItem('ANMS-AUTH', JSON.stringify(storedUserData));
  }

  currentUserIsLoggedInWithBankId() {
    return this.userData.User.LoginType === 'bankid';
  }

  currentUserHasPersonalNumberSet() {
    return this.userData.User.PersonalNumber != null ? true : false;
  }

  saveToken(response: any, username: string) {

    // login successful if there's a jwt token in the response
    if (this.isJwtLoginResponse(response)) {

      response = this.preserveActiveImpersonation(response);

      this.userData = response;
      this.userData.isAuthenticated = true;
      this.token = response.Jwt;

      this.store.dispatch(new ActionAuthLogin());
      localStorage.setItem('ANMS-AUTH', JSON.stringify(response));
      this.scheduleImpersonationExpiry();
      this.ensureCurrentUserIsLoaded();

      // set token property
      //this.token = response.Jwt;
      //
      //this.userUpdated('');

      // store username and jwt token in local storage to keep user logged in between page refreshes
      //var userString = JSON.stringify({ isAuthenticated: true, user: response.User,  username: username, token: response });
      //localStorage.setItem('ANMS-AUTH', userString);
      //var currentUserObject = JSON.parse(userString);


    }
    //this.token = currentUserObject.token.access_token;

    //  this.getCurrentUser()
    //    .subscribe(
    //      data => {

    //        const userString = localStorage.getItem('ANMS-AUTH');
    //        const currentUserObject = JSON.parse(userString);

    //        currentUserObject.User = data;
    //        localStorage.setItem('ANMS-AUTH', JSON.stringify(currentUserObject));

    //        this.userData = JSON.parse(JSON.stringify(currentUserObject));

    //        console.log('userData from api')
    //        console.log(this.userData)

    //      },
    //      error => {
    //        alert('err');
    //      });

    //  return true;
    //} else {
    //  // return false to indicate failed login
    //  return false;
    //}
  }

  userHasRole(roleName: string) {

    if (this.userData == undefined || this.userData.User == undefined || this.userData.User.Roles == null || this.userData.User.Roles.length == 0) {
      return false;
    }

    for (var i = 0; i < this.userData.User.Roles.length; i++) {
      if (this.userData.User.Roles[i].Name == roleName)
        return true;
    }

    return false;
  }

  requestResetPassword(user: User, appId: number): Observable<boolean> {

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    var data = JSON.stringify(user);

    return this.http.post<any>(this.apiBaseUrl + 'Users/ForgotPassword?appId=' + appId, data, httpOptions)
      .pipe(
        tap((response: any) =>
          this.requestResetPasswordFinished()
        ),
        catchError(this.handleError('login', data)
        ));
  }

  requestResetPasswordFinished() {
    let config = new MatSnackBarConfig();
    config.duration = 7000;

    this.snackBar.open("E-postmeddelande skickat!", undefined, config);
    this.router.navigateByUrl("/login");
  }

  resetPassword(request: PasswordReset): Observable<boolean> {

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    var data = JSON.stringify(request);

    return this.http.post<any>(this.apiBaseUrl + 'Users/ResetPassword', data, httpOptions)
      .pipe(
        tap((response: any) =>
          this.resetPasswordFinished()
        ),
        catchError(this.handleError('resetpassword', data)
        ));
  }

  resetPasswordFinished() {
    let config = new MatSnackBarConfig();
    config.duration = 7000;

    this.snackBar.open("Lösenordet återställt!", undefined, config);
    this.router.navigateByUrl("/login");
  }

  logout(): void {
    if (this.isImpersonating()) {
      this.http.delete(this.apiBaseUrl + 'impersonation', { withCredentials: true })
        .subscribe({
          next: () => this.completeLogout(),
          error: () => this.completeLogout()
        });
      return;
    }

    this.completeLogout();
  }

  private completeLogout(): void {
    if (this.impersonationTimeout) {
      clearTimeout(this.impersonationTimeout);
      this.impersonationTimeout = null;
    }

    this.token = "";
    localStorage.removeItem('currentUser');
    localStorage.removeItem('ANMS-AUTH');

    this.store.dispatch(new ActionAuthLogout());

    location.reload();
  }

  private scheduleImpersonationExpiry(): void {
    if (this.impersonationTimeout) {
      clearTimeout(this.impersonationTimeout);
      this.impersonationTimeout = null;
    }

    if (!this.isImpersonating() || !this.userData.ExpiresAtUtc) {
      return;
    }

    const millisecondsUntilExpiry = new Date(this.userData.ExpiresAtUtc).getTime() - Date.now();
    if (millisecondsUntilExpiry <= 0) {
      this.completeLogout();
      return;
    }

    this.impersonationTimeout = setTimeout(() => this.completeLogout(), millisecondsUntilExpiry);
  }

  clearLoginStorage() {
    this.localStorageService.removeItem('currentUser');
    this.localStorageService.setItem("AUTH", "{ isAuthenticated: false }");
  }

  private isJwtLoginResponse(response: any): boolean {
    return !!response && !!response.Jwt;
  }

  private preserveActiveImpersonation(response: any): any {
    if (
      response?.IsImpersonating === true ||
      !this.isImpersonating() ||
      response?.User?.LoginType !== 'impersonation'
    ) {
      return response;
    }

    return {
      ...response,
      Jwt: this.token,
      IsImpersonating: true,
      ActorDisplayName: this.userData.ActorDisplayName,
      ExpiresAtUtc: this.userData.ExpiresAtUtc
    };
  }

}



@Injectable()
export class User {
  constructor
    (
      public Email: string,
      public PersonalNumber: string,
      public username: string,
      public password: string
    ) { }
}

@Injectable()
export class PasswordReset {
  constructor
    (
      public Email: string,
      public Password: string,
      public ConfirmPassword: string,
      public Code: string
    ) { }
}


